import React, { FormEvent } from "react";
import { matchCase } from "src/utils";
import { ValidationErrors } from "src/errors";
import { SendPurchaseOrderFormDataKey, SendPurchaseOrderFormData } from "src/types";
import { SendPurchaseOrderEmailSettingsToolTip } from "src/components/tooltips/purchaseOrdersTooltips";
import { TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { useScrollOnMessage } from "src/hooks";

interface SendPurchaseOrderFormProps {
  formData: SendPurchaseOrderFormData;
  purchaseOrderOptions?: string[];
  vendorsOptions?: string[];
  validationErrors?: ValidationErrors<SendPurchaseOrderFormDataKey>;
  formLvlErrorMessage: string;
  handleInputChange: (
    field: SendPurchaseOrderFormDataKey,
    value: SendPurchaseOrderFormData[keyof SendPurchaseOrderFormData]
  ) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  setAction: (action: "SEND" | "PREVIEW") => void;
}

const SendPurchaseOrderForm: React.FC<SendPurchaseOrderFormProps> = ({
  formData,
  purchaseOrderOptions,
  vendorsOptions,
  validationErrors,
  formLvlErrorMessage,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Send Purchase Order</h1>
            <p className="text-primary-100 mt-2 text-lg">Create and send purchase orders to vendors</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            {/* Purchase Order Details Card */}
            <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
              <CollapsibleSection
                initiallyOpen={true}
                title={
                  <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-primary-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Purchase Order Details
                  </h3>
                }
              >
                <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-2">Purchase Order Number</label>
                    <TextInputBox
                      id="purchase-order-number"
                      placeholder="Purchase Order Number"
                      onInputChange={(value) => handleInputChange("purchaseOrderId", value.toUpperCase())}
                      inputValue={formData.purchaseOrderId}
                      dropDownOptions={purchaseOrderOptions}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderId")}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-2">Vendor</label>
                    <TextInputBox
                      id="vendor"
                      placeholder="Vendor"
                      onInputChange={(value) => handleInputChange("vendorName", value)}
                      inputValue={formData.vendorName}
                      onBlur={() =>
                        handleInputChange("vendorName", matchCase(formData.vendorName, vendorsOptions ?? []))
                      }
                      dropDownOptions={vendorsOptions}
                    />
                    <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("vendorName")} />
                  </div>

                  <div className="lg:col-span-2 space-y-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Additional Information
                      <span className="text-secondary-500 text-xs ml-2">(optional)</span>
                    </label>
                    <TextInputBoxLarge
                      id="po-additional-info"
                      placeholder="Enter additional information for the purchase order..."
                      onInputChange={(value) => handleInputChange("additionalInfo", value)}
                      inputValue={formData.additionalInfo}
                      maxLength={1200}
                      rows={5}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("additionalInfo")}
                    />
                  </div>
                </div>
              </CollapsibleSection>
            </div>

            {/* Form Level Error Message */}
            <div className="mt-6">
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-between items-center pt-6 border-t border-secondary-200">
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <button
                  className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    !formData.purchaseOrderId || !formData.vendorName
                      ? "bg-secondary-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:ring-primary-500 shadow-lg hover:shadow-xl"
                  }`}
                  onClick={() => setAction("PREVIEW")}
                  type="submit"
                  disabled={!formData.purchaseOrderId || !formData.vendorName}
                >
                  <span className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    Preview
                  </span>
                </button>

                <button
                  onClick={() => setAction("SEND")}
                  className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    !formData.purchaseOrderId || !formData.vendorName
                      ? "bg-secondary-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-success-600 to-success-700 hover:from-success-700 hover:to-success-800 focus:ring-success-500 shadow-lg hover:shadow-xl"
                  }`}
                  type="submit"
                  disabled={!formData.purchaseOrderId || !formData.vendorName}
                >
                  <span className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Send
                  </span>
                </button>
                <SendPurchaseOrderEmailSettingsToolTip />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { SendPurchaseOrderForm };
